// Client-side geometry, for the two numbers the detail sheet shows that the server has no way
// to know: how far the aircraft is from *this device*, and in which direction.
//
// Deliberately not asked of the backend. The phone's own position is the honest reference point
// while you are standing outside looking up, it changes as you walk, and sending it to the
// server on every pan to get a distance back would be a round trip for arithmetic. The saved
// home anchor the alert engine uses is a different thing, and arrives with alerts in Phase 4.

const EARTH_RADIUS_M = 6_371_008.8;
const rad = (deg: number) => (deg * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

export function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p1 = rad(lat1);
  const p2 = rad(lat2);
  const dp = p2 - p1;
  const dl = rad(lon2 - lon1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p1 = rad(lat1);
  const p2 = rad(lat2);
  const dl = rad(lon2 - lon1);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (deg(Math.atan2(y, x)) + 360) % 360;
}

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

/** "NE" is a direction you can act on while standing in a garden; "42°" is not. */
export function compassPoint(bearing: number): string {
  return COMPASS[Math.round(bearing / 22.5) % 16];
}

export function formatDistance(metres: number): string {
  if (metres < 1000) return `${Math.round(metres / 10) * 10} m`;
  if (metres < 10_000) return `${(metres / 1000).toFixed(1)} km`;
  return `${Math.round(metres / 1000)} km`;
}

/** Where you end up travelling `distanceM` along `bearing` — the same maths as the backend's
 *  radar/geo.py, used here only to glide markers between server ticks. */
export function destinationPoint(lat: number, lon: number, bearing: number, distanceM: number): [number, number] {
  const delta = distanceM / EARTH_RADIUS_M;
  const theta = rad(bearing);
  const p1 = rad(lat);
  const l1 = rad(lon);
  const p2 = Math.asin(Math.sin(p1) * Math.cos(delta) + Math.cos(p1) * Math.sin(delta) * Math.cos(theta));
  const l2 = l1 + Math.atan2(Math.sin(theta) * Math.sin(delta) * Math.cos(p1), Math.cos(delta) - Math.sin(p1) * Math.sin(p2));
  return [deg(p2), ((deg(l2) + 540) % 360) - 180];
}
